import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';

// Types
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
}

export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  message?: string;
}

// UUID generation fallback for browsers that don't support crypto.randomUUID
const generateUUID = (): string => {
  // Try native crypto.randomUUID first
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Configuration
const API_CONFIG = {
  orchestrator: import.meta.env.VITE_API_ORCHESTRATOR_URL || '/api/orchestrator',
  core: import.meta.env.VITE_API_CORE_URL || '/api/core',
  auth: import.meta.env.VITE_API_AUTH_URL || '/api/auth',
  timeout: 30000,
  retries: 3,
  retryDelay: 1000,
};

// Create axios instances
const createApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    timeout: API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable cookies for auth
  });

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      // User auth is now handled via HTTP-only cookies, no Bearer tokens needed
      // Remove any potential Authorization header that might conflict with cookie auth
      if (config.headers && config.headers.Authorization) {
        delete config.headers.Authorization;
      }

      // Add request ID for tracking
      config.headers['X-Request-ID'] = generateUUID();

      return config;
    },
    (error) => {
      console.error('Request error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor with retry logic
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as AxiosRequestConfig & { _retry?: number };

      // Handle case where config is undefined (network errors, CORS, etc)
      if (!config) {
        console.error('Request failed before sending:', error.message);
        return Promise.reject(error);
      }

      // Initialize retry count
      if (!config._retry) {
        config._retry = 0;
      }

      // Check if we should retry
      if (
        config._retry < API_CONFIG.retries &&
        error.response &&
        [502, 503, 504].includes(error.response.status)
      ) {
        config._retry++;

        // Exponential backoff
        const delay = API_CONFIG.retryDelay * Math.pow(2, config._retry - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));

        return client(config);
      }

      // Transform error for consistent handling
      const responseData = error.response?.data as unknown;
      const apiError: ApiError = {
        message: responseData && typeof responseData === 'object' && 'message' in responseData
          ? String((responseData as { message?: string }).message)
          : error.message || 'An error occurred',
        status: error.response?.status,
        code: error.code,
        details: responseData,
      };

      return Promise.reject(apiError);
    }
  );

  return client;
};

// Export configured clients
export const orchestratorClient = createApiClient(API_CONFIG.orchestrator);
export const coreClient = createApiClient(API_CONFIG.core);
export const authClient = createApiClient(API_CONFIG.auth);

// Helper functions
export const handleApiError = (error: ApiError): string => {
  if (error.status === 401) {
    // Handle authentication error - redirect to login (cookies will be cleared server-side)
    window.location.href = '/login';
    return 'Authentication required';
  }

  if (error.status === 403) {
    return 'You do not have permission to perform this action';
  }

  if (error.status === 404) {
    return 'Resource not found';
  }

  if (error.status === 429) {
    return 'Too many requests. Please try again later.';
  }

  if (error.status && error.status >= 500) {
    return 'Server error. Please try again later.';
  }

  return error.message || 'An unexpected error occurred';
};

// Request helpers
export const apiRequest = async <T = unknown>(
  client: AxiosInstance,
  config: AxiosRequestConfig
): Promise<T> => {
  try {
    const response = await client(config);
    return response.data;
  } catch (error) {
    throw error as ApiError;
  }
};

// Export types and utilities
export type { AxiosInstance, AxiosRequestConfig };
